import { useEffect, useMemo, useRef, useState } from 'react';

import {
  appDelay,
  Buyer,
  canUserAccessBuyerBranch,
  DocumentsSummary,
  getBuyerFiservFeeRate,
} from '@dill/dill-shared';
import { formatPhoneAsYouType, isValidPhone, toE164 } from '../../../../../utils/phone';
import { DillModal } from '../../../../general/Dill/DillModal';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import {
  closeMessageModal,
  openMessageModal,
} from '../../../../../redux/globalSlices/genericSlice';
import { AppDropDown2 } from '../../../../general/AppDropDown2/AppDropDown2';
import { AppInputField } from '../../../../general/AppInputField/AppInputField';
import AppFilePicker from '../../../../general/AppFilePicker/AppFilePicker';
import { createPaymentRequest } from '../../../../../redux/services/paymentRequestsService';
import AppRangeSlider from '../../../../general/AppAppRangeSlider/AppRangeSlider';
import { AppRadioButton } from '../../../../general/AppRadioButton/AppRadioButton';
import { AppSwitch } from '../../../../general/AppSwitch/AppSwitch';
import { DillCheckbox } from '../../../../general/Dill/DillCheckbox';
import { DillButton } from '../../../../general/Dill/DillButton';
import { useFIFOEffect } from '../../../../../utils/hooks/useFIFOEffect';
import { twMerge } from 'tailwind-merge';
import {
  getUserCustomers,
  recursiveGetUserCustomers,
} from '../../../../../redux/services/suppliersService';

export const AddPaymentRequestModal = ({
  open,
  handleClose = () => {},
  onPaymentRequestAdded = () => {},
}: {
  open: boolean;
  handleClose: () => void;
  onPaymentRequestAdded: () => void;
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  // Tracks the active supplier id for in-flight customer fetches, so a chain started for a
  // previous supplier stops once the supplier is switched (otherwise its results would be
  // written into the now differently-scoped store). The request ref lets us abort in-flight.
  const activeFetchSupplierIdRef = useRef<string | null>(currentSupplier?.id ?? null);
  const inFlightCustomersRequestRef = useRef<{ abort: () => void } | null>(null);
  useEffect(() => {
    activeFetchSupplierIdRef.current = currentSupplier?.id ?? null;
  }, [currentSupplier?.id]);
  useEffect(() => () => inFlightCustomersRequestRef.current?.abort(), [currentSupplier?.id]);
  const [isShowErrors, setIsShowErrors] = useState(false);
  const [whoPaysFee, setWhoPaysFee] = useState<'SUPPLIER' | 'BUYER' | 'BOTH'>('SUPPLIER');
  const [deliveryMethod, setDeliveryMethod] = useState<'EMAIL' | 'SMS' | 'BOTH'>('EMAIL');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [hasSmsConsent, setHasSmsConsent] = useState(false);

  // const { supplierBuyers } = useAppSelector((state) => state.supplierBuyers);
  const { customers } = useAppSelector((state) => state.supplier);

  const [paymentAmount, setPaymentAmount] = useState<number | null>(null);
  const [hasBadAmountInput, setHasBadAmountInput] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<{
    buyer: Buyer;
    usersEmailsDetails: {
      email: string;
      isStatementsEmailOptOut: boolean;
      isPaymentRemindersEmailOptOut: boolean;
      isInvitedUser: boolean;
    }[];
    documentsSummary: DocumentsSummary;
  } | null>(null);
  const [customerEmails, setCustomerEmails] = useState<string[]>([]);
  const [email, setEmail] = useState('');
  const [customerPhoneNumbers, setCustomerPhoneNumbers] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [prePaymentAttachmentsFiles, setPrePaymentAttachmentsFiles] = useState<File[]>([]);
  const [supplierTakeRate, setSupplierTakeRate] = useState(0);
  const [buyerTakeRate, setBuyerTakeRate] = useState(0);
  const [prePaymentAttachmentsUrls, setPrePaymentAttachmentsUrls] = useState<
    {
      name: string;
      url: string;
    }[]
  >([]);
  const [is3DSecure, setIs3DSecure] = useState(false);
  const [isBankEnabled, setIsBankEnabled] = useState(true);
  const [isCreditCardEnabled, setIsCreditCardEnabled] = useState(true);
  const [isDebitCardEnabled, setIsDebitCardEnabled] = useState(true);
  const [customerCount, setCustomerCount] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [previousLastDocumentId, setPreviousLastDocumentId] = useState('');

  // A branch-scoped user can only request payment from customers in their branches — the backend
  // rejects anything else, so don't offer it here either.
  const fetchedSupplierBuyers = useMemo(
    () =>
      customers.filter((customerDetails) =>
        canUserAccessBuyerBranch({
          user,
          supplierId: currentSupplier?.id,
          buyerBranch: customerDetails?.buyer?.branch,
        })
      ),
    [customers, user, currentSupplier]
  );

  const recursiveFetchCustomers = async (
    count: number,
    lastDockId: string,
    firstCustomers: {
      buyer: Buyer;
      usersEmailsDetails: {
        email: string;
        userId: string;
        isInvitedUser: boolean;
        isStatementsEmailOptOut: boolean;
        isPaymentRemindersEmailOptOut: boolean;
      }[];
      documentsSummary: DocumentsSummary;
    }[],
    supplierId: string
  ) => {
    if (firstCustomers.length > count) {
      return;
    }
    setCustomerCount(count);
    const pageSize = 100;
    let currentPreviousLastDocumentId = lastDockId;
    for (let i = firstCustomers.length; i < count; i += pageSize) {
      // A newer fetch (e.g. after switching suppliers) supersedes this loop — stop so we
      // don't write the previous supplier's customers into the store.
      if (!supplierId || activeFetchSupplierIdRef.current !== supplierId) {
        break;
      }
      const request = dispatch(
        recursiveGetUserCustomers({
          supplierId,
          previousLastDocumentId: currentPreviousLastDocumentId,
          pageSize,
          sortBy: 'name',
          sortOrder: 'asc',
        })
      );
      inFlightCustomersRequestRef.current = request;
      const results = await request;
      if (activeFetchSupplierIdRef.current !== supplierId) {
        break;
      }
      if (results.type === 'supplier/recursiveGetUserCustomers/fulfilled') {
        if (results.payload) {
          const payload = results.payload as any;
          const fetchedCustomers: {
            buyer: Buyer;
            usersEmailsDetails: {
              email: string;
              isInvitedUser: boolean;
              isStatementsEmailOptOut: boolean;
              isPaymentRemindersEmailOptOut: boolean;
            }[];
            documentsSummary: DocumentsSummary;
          }[] = payload.data as any;
          const lastDocumentId = payload.lastDocumentId;
          if (lastDocumentId !== currentPreviousLastDocumentId) {
            setPreviousLastDocumentId(lastDocumentId);
            currentPreviousLastDocumentId = lastDocumentId;
          }
          if (!lastDockId || fetchedCustomers.length < pageSize) {
            console.log('No more pages to fetch');
            break;
          }
        }
      } else {
        console.log('Fetch failed or interrupted');
        break;
      }
    }
  };

  const handleLoadMore = async (loadOptions?: { isFreshFetch?: boolean }) => {
    const fetchSupplierId = currentSupplier?.id || '';
    if (!fetchSupplierId) {
      return;
    }
    setLoading(true);
    const pageSize = 100;
    let currentPreviousLastDocumentId = previousLastDocumentId;
    if (loadOptions && loadOptions?.isFreshFetch) {
      currentPreviousLastDocumentId = '';
    }
    try {
      const request = dispatch(
        getUserCustomers({
          supplierId: fetchSupplierId,
          previousLastDocumentId: currentPreviousLastDocumentId,
          pageSize,
          sortBy: 'name',
          sortOrder: 'asc',
        })
      );
      inFlightCustomersRequestRef.current = request;
      const results = await request;
      if (activeFetchSupplierIdRef.current !== fetchSupplierId) {
        return;
      }
      if (results.type === 'supplier/getUserCustomers/fulfilled') {
        if (results.payload) {
          const payload = results.payload as any;
          const cust: {
            buyer: Buyer;
            usersEmailsDetails: {
              email: string;
              userId: string;
              isInvitedUser: boolean;
              isStatementsEmailOptOut: boolean;
              isPaymentRemindersEmailOptOut: boolean;
            }[];
            documentsSummary: DocumentsSummary;
          }[] = payload.data as any;
          const lastDockId = payload.lastDocumentId;
          if (!customerCount && payload.customerCount) {
            setCustomerCount(payload.customerCount || 0);
          }
          if (lastDockId && lastDockId !== currentPreviousLastDocumentId) {
            setPreviousLastDocumentId(lastDockId);

            const count = Number(payload.customerCount);
            // Awaited so the dropdown keeps showing the loading indicator until every page
            // of customers has streamed in, not just the first one.
            await recursiveFetchCustomers(count, lastDockId, [...cust], fetchSupplierId);
          }
        }
      }
    } finally {
      // A newer fetch (e.g. after switching suppliers) owns the loading state now.
      if (activeFetchSupplierIdRef.current === fetchSupplierId) {
        setLoading(false);
      }
    }
  };

  // Allowed payment methods come from the selected customer when there is one, and fall back to
  // the supplier's defaults for new customers, matching how the backend seeds buyers.
  const applyDisabledPaymentTypes = (
    disabledPaymentTypes?: ('CREDIT_CARD' | 'DEBIT_CARD' | 'BANK')[] | null
  ) => {
    const disabledTypes = disabledPaymentTypes || [];
    setIsBankEnabled(!disabledTypes.includes('BANK'));
    setIsCreditCardEnabled(!disabledTypes.includes('CREDIT_CARD'));
    setIsDebitCardEnabled(!disabledTypes.includes('DEBIT_CARD'));
  };

  useEffect(() => {
    if (currentSupplier) {
      setSupplierTakeRate(currentSupplier?.creditCardTakeRate || 3.5);
      if (currentSupplier?.cybersourceSettings?.is3DSEnabled) {
        setIs3DSecure(true);
      }
    }
    return () => {};
  }, [currentSupplier]);

  useEffect(() => {
    applyDisabledPaymentTypes(
      selectedCustomer
        ? selectedCustomer.buyer?.disabledPaymentTypes
        : currentSupplier?.defaultBuyerDisabledPaymentTypes
    );
    return () => {};
  }, [selectedCustomer, currentSupplier]);

  useFIFOEffect(async () => {
    if (currentSupplier?.cardPaymentProvider === 'FISERV') {
      await appDelay(100);
      setBuyerTakeRate(
        getBuyerFiservFeeRate({
          buyer: selectedCustomer?.buyer,
          supplier: currentSupplier,
        })
      );
    }
  }, [selectedCustomer, currentSupplier]);

  useEffect(() => {
    if (selectedCustomer) {
      setCustomerEmails([
        ...(selectedCustomer.usersEmailsDetails.map((email1) => email1.email) || []),
      ]);
      setCustomerPhoneNumbers(selectedCustomer.buyer.phoneNumbers || []);
    }
  }, [selectedCustomer]);

  useEffect(() => {
    if (deliveryMethod === 'EMAIL') {
      setPhoneNumber('');
      setHasSmsConsent(false);
    }

    return () => {};
  }, [deliveryMethod]);

  useEffect(() => {
    if (open) {
      setIsShowErrors(false);
      setSelectedCustomer(null);
      setEmail('');
      setPaymentAmount(null);
      setHasBadAmountInput(false);
      setNotes('');
      setDeliveryMethod('EMAIL');
      setPhoneNumber('');
      setHasSmsConsent(false);
      handleLoadMore({ isFreshFetch: true });
      setBuyerTakeRate(
        currentSupplier
          ? getBuyerFiservFeeRate({
              buyer: selectedCustomer?.buyer,
              supplier: currentSupplier,
            })
          : 0
      );
      applyDisabledPaymentTypes(currentSupplier?.defaultBuyerDisabledPaymentTypes);
    }
    if (!open) {
      setIsShowErrors(false);
      setSelectedCustomer(null);
      setEmail('');
      setPaymentAmount(null);
      setHasBadAmountInput(false);
      setNotes('');
      setDeliveryMethod('EMAIL');
      setPhoneNumber('');
      setHasSmsConsent(false);
      applyDisabledPaymentTypes(currentSupplier?.defaultBuyerDisabledPaymentTypes);
    }

    return () => {};
  }, [open]);

  const hasNoEnabledPaymentMethod = !isBankEnabled && !isCreditCardEnabled && !isDebitCardEnabled;

  const checkForErrors = () => {
    let isError = false;

    if (!selectedCustomer) {
      isError = true;
    }
    if (hasNoEnabledPaymentMethod) {
      isError = true;
    }
    if (deliveryMethod === 'EMAIL' || deliveryMethod === 'BOTH') {
      if (!email) {
        isError = true;
      }
      if (email && !/[\w.+-]+@[\w-]+\.[a-z]{1,}/gi.test(email)) {
        isError = true;
      }
    }
    if (deliveryMethod === 'SMS' || deliveryMethod === 'BOTH') {
      if (!phoneNumber) {
        isError = true;
      }
      if (phoneNumber && !isValidPhone(phoneNumber)) {
        isError = true;
      }
      if (!hasSmsConsent) {
        isError = true;
      }
    }
    if (!notes) {
      isError = true;
    }
    if (!paymentAmount) {
      isError = true;
    }
    return isError;
  };

  const handlePay = async () => {
    setIsShowErrors(true);
    console.log({ ooo: toE164(phoneNumber) });
    if (checkForErrors() || !paymentAmount) {
      return;
    }

    const disabledPaymentTypes: ('CREDIT_CARD' | 'DEBIT_CARD' | 'BANK')[] = [];
    if (!isCreditCardEnabled) disabledPaymentTypes.push('CREDIT_CARD');
    if (!isDebitCardEnabled) disabledPaymentTypes.push('DEBIT_CARD');
    if (!isBankEnabled) disabledPaymentTypes.push('BANK');

    const paymentRequestResponse = await dispatch(
      createPaymentRequest({
        buyerId: selectedCustomer?.buyer?.id || '',
        supplierId: currentSupplier?.id || '',
        email: deliveryMethod === 'SMS' ? null : email,
        notes,
        paymentAmount,
        whoPaysFee,
        buyerTakeRate,
        attachmentsFiles: prePaymentAttachmentsFiles,
        is3DSecure,
        phoneNumber:
          deliveryMethod === 'SMS' || deliveryMethod === 'BOTH' ? toE164(phoneNumber) : null,
        deliveryMethod,
        disabledPaymentTypes,
      })
    );
    if (paymentRequestResponse.type === 'paymentRequests/createPaymentRequest/fulfilled') {
      const dataResults: any = paymentRequestResponse.payload;
      if (dataResults && dataResults?.data?.data) {
      }
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'SUCCESS',
          title: 'Payment Request successfully sent!',
          subTitle: `Please check your Transaction History for confirmation of final payment status`,
          onClose: () => {
            onPaymentRequestAdded();
          },
        })
      );
    } else {
      let errorMessage = '';
      errorMessage = paymentRequestResponse.payload as any;
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Error requesting payment',
          subTitle: `Contact customer support. ${errorMessage}`,
        })
      );
    }
  };
  useEffect(() => {
    if (buyerTakeRate === 0) {
      setWhoPaysFee('SUPPLIER');
    } else if (buyerTakeRate === supplierTakeRate) {
      setWhoPaysFee('BUYER');
    } else {
      setWhoPaysFee('BOTH');
    }

    return () => {};
  }, [buyerTakeRate]);

  const sectionClass = 'flex flex-col gap-3 p-4 rounded-lg border border-slate-200 bg-white';
  const sectionLabelClass = 'text-xs font-semibold text-slate-500 uppercase tracking-wide';

  return (
    <DillModal
      open={open}
      onClose={handleClose}
      title="Request Payment"
      maxWidth="640px"
      footer={
        <>
          <DillButton label="Close" variant="secondary" onClick={handleClose} />
          <DillButton label="Request" variant="primary" onClick={handlePay} />
        </>
      }>
      <div className="flex flex-col gap-4">
        {/* Customer */}
        <div className={sectionClass}>
          <p className={sectionLabelClass}>Customer</p>
          <AppDropDown2
            isRequired
            label="Customer"
            isLoading={loading}
            placeholder={loading && fetchedSupplierBuyers.length === 0 ? 'Loading...' : 'Select'}
            errorText={isShowErrors && !selectedCustomer ? 'Required' : ''}
            value={selectedCustomer?.buyer?.id || ''}
            items={[
              ...fetchedSupplierBuyers
                .filter((customerDetails) => !customerDetails?.buyer?.isDeactivated)
                .map((customer) => {
                  return {
                    label: customer.buyer.name,
                    value: customer.buyer.id,
                    labelInElement: (
                      <div className="flex px-2 py-3 m-[-5px] items-center justify-between group relative">
                        <div className="text-sm">{`${customer.buyer.name}`}</div>
                      </div>
                    ),
                  };
                }),
            ]}
            onSelectChange={(item) => {
              if (item && item?.value !== null) {
                const foundSupplierBuyer = fetchedSupplierBuyers.find(
                  (supBuy) => supBuy.buyer.id === item?.value
                );
                if (foundSupplierBuyer) {
                  setSelectedCustomer(foundSupplierBuyer);
                }
              }
            }}
          />
        </div>

        {/* Delivery */}
        <div className={sectionClass}>
          <p className={sectionLabelClass}>Delivery</p>
          <div className="flex flex-col gap-2">
            <p className="text-xs font-medium text-slate-700">Delivery Method</p>
            <div className="flex gap-4">
              <div
                className="flex items-center cursor-pointer"
                onClick={() => setDeliveryMethod('EMAIL')}>
                <AppRadioButton isChecked={deliveryMethod === 'EMAIL'} />
                <p className="text-xs ml-1 font-medium">Email</p>
              </div>
              <div
                className="flex items-center cursor-pointer"
                onClick={() => setDeliveryMethod('SMS')}>
                <AppRadioButton isChecked={deliveryMethod === 'SMS'} />
                <p className="text-xs ml-1 font-medium">SMS</p>
              </div>
              <div
                className="flex items-center cursor-pointer"
                onClick={() => setDeliveryMethod('BOTH')}>
                <AppRadioButton isChecked={deliveryMethod === 'BOTH'} />
                <p className="text-xs ml-1 font-medium">Both</p>
              </div>
            </div>
          </div>

          {(deliveryMethod === 'EMAIL' || deliveryMethod === 'BOTH') && (
            <AppDropDown2
              isRequired={deliveryMethod === 'EMAIL' || deliveryMethod === 'BOTH'}
              label="Email"
              errorText={
                isShowErrors && (deliveryMethod === 'EMAIL' || deliveryMethod === 'BOTH') && !email
                  ? 'Required'
                  : isShowErrors &&
                    (deliveryMethod === 'EMAIL' || deliveryMethod === 'BOTH') &&
                    !/[\w.+-]+@[\w-]+\.[a-z]{1,}/gi.test(email)
                  ? 'Invalid email'
                  : ''
              }
              value={email || ''}
              isCreatable={true}
              onCreateNewItem={(newValue) => {
                setCustomerEmails([
                  ...(selectedCustomer?.usersEmailsDetails.map((email1) => email1.email) || []),
                  newValue,
                ]);
                setEmail(newValue);
              }}
              items={[
                ...customerEmails.map((emailDetails) => {
                  return {
                    label: emailDetails,
                    value: emailDetails,
                    labelInElement: (
                      <div className="flex px-2 py-3 m-[-5px] items-center justify-between group relative">
                        <div className="text-sm">{`${emailDetails}`}</div>
                      </div>
                    ),
                  };
                }),
              ]}
              onSelectChange={(item) => {
                setEmail(item.value);
              }}
            />
          )}

          {(deliveryMethod === 'SMS' || deliveryMethod === 'BOTH') && (
            <AppDropDown2
              isRequired={deliveryMethod === 'SMS' || deliveryMethod === 'BOTH'}
              label="Phone Number"
              errorText={
                isShowErrors &&
                (deliveryMethod === 'SMS' || deliveryMethod === 'BOTH') &&
                !phoneNumber
                  ? 'Required'
                  : isShowErrors &&
                    (deliveryMethod === 'SMS' || deliveryMethod === 'BOTH') &&
                    phoneNumber &&
                    !isValidPhone(phoneNumber)
                  ? 'Please enter a valid phone number (e.g. 650-922-0999).'
                  : ''
              }
              value={phoneNumber || ''}
              isCreatable={true}
              formatInput={(rawValue) => formatPhoneAsYouType(rawValue)}
              onCreateNewItem={(newValue) => {
                setCustomerPhoneNumbers([
                  ...(selectedCustomer?.buyer.phoneNumbers || []),
                  newValue,
                ]);
                setPhoneNumber(newValue);
              }}
              items={[
                ...customerPhoneNumbers.map((phoneNumberDetails) => {
                  return {
                    label: phoneNumberDetails,
                    value: phoneNumberDetails,
                    labelInElement: (
                      <div className="flex px-2 py-3 m-[-5px] items-center justify-between group relative">
                        <div className="text-sm">{`${phoneNumberDetails}`}</div>
                      </div>
                    ),
                  };
                }),
              ]}
              onSelectChange={(item) => {
                setPhoneNumber(item.value);
              }}
            />
          )}

          {(deliveryMethod === 'SMS' || deliveryMethod === 'BOTH') && (
            <div className="flex flex-col">
              <div
                className={twMerge(
                  `p-3 bg-slate-50 rounded-lg border`,
                  isShowErrors && !hasSmsConsent ? 'border-red-500' : 'border-slate-200'
                )}>
                <div className="flex items-start gap-2">
                  <DillCheckbox
                    isChecked={hasSmsConsent}
                    onChange={(checked: boolean) => setHasSmsConsent(checked)}
                    className={twMerge(isShowErrors && !hasSmsConsent ? 'border-red-500' : '')}
                  />
                  <label htmlFor="sms-consent" className="text-xs text-gray-700 cursor-pointer">
                    <span className="text-red-500 mr-1">*</span>
                    <span className="font-bold">
                      I have read the following script to the customer and obtained express verbal
                      consent to send a single SMS containing the requested payment link.
                    </span>
                  </label>
                </div>
                <div className="mt-2 ml-6 p-2 bg-white rounded border border-slate-200">
                  <p className="text-xs text-gray-600 italic">
                    <span className="font-medium">Consent verbal script:</span> "Do you agree to
                    receive a one-time SMS from {currentSupplier?.name || '[Supplier Name]'}{' '}
                    containing your requested payment link? Message frequency is one message per
                    request. Message and data rates may apply. Reply STOP to opt out or HELP for
                    help. <span>View the privacy policy here </span>
                    <a
                      href="https://usedill.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline">
                      https://usedill.com/privacy
                    </a>
                    <span> and terms of service here </span>
                    <a
                      href="https://usedill.com/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline">
                      https://usedill.com/terms
                    </a>
                  </p>
                </div>
              </div>
              {isShowErrors && !hasSmsConsent && (
                <span className="text-red-500 ml-1 text-2xs mt-0.5">Required</span>
              )}
            </div>
          )}
        </div>

        {/* Payment Settings */}
        <div className={sectionClass}>
          <p className={sectionLabelClass}>Payment Settings</p>

          <div className="flex flex-col gap-2">
            <p className="text-xs font-medium text-slate-700">
              Set the fees for credit card payments only. This will not apply to debit cards, as
              surcharging the customer on debit cards is not permitted according to federal law.
            </p>
            <div className="flex flex-col gap-1">
              <div
                className="flex items-center cursor-pointer w-full"
                onClick={() => {
                  setWhoPaysFee('SUPPLIER');
                  setBuyerTakeRate(0);
                }}>
                <AppRadioButton isChecked={whoPaysFee === 'SUPPLIER'} />
                <p className="text-xs ml-1 font-medium">Fees paid by supplier</p>
              </div>
              <div
                className="flex items-center cursor-pointer w-full"
                onClick={() => {
                  setWhoPaysFee('BOTH');
                  if (currentSupplier?.cardPaymentProvider === 'FISERV') {
                    setBuyerTakeRate(
                      getBuyerFiservFeeRate({
                        buyer: selectedCustomer?.buyer,
                        supplier: currentSupplier,
                      })
                    );
                  } else {
                    setBuyerTakeRate(supplierTakeRate / 2);
                  }
                }}>
                <AppRadioButton isChecked={whoPaysFee === 'BOTH' || whoPaysFee === 'BUYER'} />
                <p className="text-xs ml-1 font-medium">Fees paid by contractor</p>
              </div>
            </div>
            {(whoPaysFee === 'BOTH' || whoPaysFee === 'BUYER') && (
              <div className="bg-slate-50 px-3 py-3 rounded-lg border border-slate-200">
                <p className="text-xs font-medium text-slate-700 mb-2">
                  Set how much of the transaction fee rate is paid by the contractor
                </p>
                <AppRangeSlider
                  key={`slider-${whoPaysFee}-${supplierTakeRate}`}
                  min={0}
                  max={supplierTakeRate}
                  valueUnits="%"
                  valueLabel="Contractor fee rate"
                  otherValueLabel={
                    currentSupplier?.cardPaymentProvider === 'FISERV' ? '' : 'Your fee rate'
                  }
                  value={buyerTakeRate}
                  onChange={({ value }) => {
                    setBuyerTakeRate(value);
                  }}
                />
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-xs font-medium text-slate-700">Allowed Payment Methods</p>
            <div className="flex gap-4">
              {(
                [
                  { label: 'Bank Transfer', value: isBankEnabled, setter: setIsBankEnabled },
                  {
                    label: 'Credit Card',
                    value: isCreditCardEnabled,
                    setter: setIsCreditCardEnabled,
                  },
                  {
                    label: 'Debit / Other Card',
                    value: isDebitCardEnabled,
                    setter: setIsDebitCardEnabled,
                  },
                ] as { label: string; value: boolean; setter: (v: boolean) => void }[]
              ).map(({ label, value, setter }) => (
                <div
                  key={label}
                  className="flex items-center gap-2 cursor-pointer"
                  onClick={() => setter(!value)}>
                  <DillCheckbox
                    isChecked={value}
                    onChange={(checked: boolean) => setter(checked)}
                  />
                  <p className="text-xs font-medium leading-tight hover:text-blue-700 cursor-pointer">
                    {label}
                  </p>
                </div>
              ))}
            </div>
            {isShowErrors && hasNoEnabledPaymentMethod && (
              <span className="text-red-500 text-2xs">Enable at least one payment method</span>
            )}
          </div>

          {(currentSupplier?.cardPaymentProvider === 'STRIPE' ||
            !currentSupplier?.cardPaymentProvider ||
            currentSupplier?.cybersourceSettings?.is3DSEnabled) && (
            <div
              className="flex items-center cursor-pointer"
              onClick={() => setIs3DSecure(!is3DSecure)}>
              <AppSwitch isChecked={is3DSecure} />
              <p className="text-sm ml-2 font-medium">3D Secure</p>
            </div>
          )}
        </div>

        {/* Payment Details */}
        <div className={sectionClass}>
          <p className={sectionLabelClass}>Payment Details</p>
          <AppInputField
            prefix="$"
            label="Amount"
            isRequired={true}
            inputType="number"
            errorText={
              isShowErrors && hasBadAmountInput
                ? 'Must be a valid number'
                : isShowErrors && (paymentAmount === null || paymentAmount === undefined)
                ? 'Required'
                : isShowErrors && paymentAmount != null && paymentAmount <= 0
                ? 'Amount has to be greater than 0'
                : ''
            }
            value={paymentAmount ?? ''}
            onBlur={(e) => {
              if (e.target.validity?.badInput) {
                setHasBadAmountInput(true);
                setPaymentAmount(null);
              } else {
                setHasBadAmountInput(false);
                const val = e.target.value;
                if (!val) {
                  setPaymentAmount(null);
                } else {
                  setPaymentAmount(+Number(val).toFixed(2));
                }
              }
            }}
            onTextChange={(text) => {
              if (text) {
                setHasBadAmountInput(false);
                setPaymentAmount(Number(text));
              } else {
                setPaymentAmount(null);
              }
            }}
            onPaste={(e) => {
              const raw = e.clipboardData.getData('text');
              const cleaned = raw.replace(/[^0-9.]/g, '');
              const parsed = parseFloat(cleaned);
              if (!isNaN(parsed)) {
                e.preventDefault();
                setHasBadAmountInput(false);
                setPaymentAmount(+parsed.toFixed(2));
              }
            }}
          />
          <AppInputField
            label="Notes"
            placeholder="Sales Order #, etc"
            value={notes}
            errorText={isShowErrors && !notes ? 'Required' : ''}
            inputType="textarea"
            isRequired={true}
            onTextChange={(text) => {
              setNotes(text);
            }}
          />
          <div>
            <div className="text-xs mb-0.5 whitespace-nowrap font-medium">Attachments</div>
            <AppFilePicker
              onSelectFile={(uploadedFiles: File[], fileUrls?: { name: string; url: string }[]) => {
                setPrePaymentAttachmentsFiles(uploadedFiles);
                if (fileUrls) {
                  setPrePaymentAttachmentsUrls(fileUrls);
                }
              }}
              fileUrls={prePaymentAttachmentsUrls}
            />
          </div>
        </div>
      </div>
    </DillModal>
  );
};
